import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import Logo from '../components/Logo';
import Footer from '../components/Footer';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Clock, Target, Award, BookOpen, Briefcase, TrendingUp, DollarSign, X, Star, Check } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : 'http://localhost:8001/api';

const CourseDetailPublic = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useContext(AuthContext);
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);

  useEffect(() => {
    fetchCourseDetails();
  }, [courseId]);

  const fetchCourseDetails = async () => {
    try {
      const response = await axios.get(`${API}/courses/${courseId}`);
      setCourse(response.data.course || response.data);
    } catch (error) {
      console.error('Error fetching course:', error);
      toast.error('Failed to load course details');
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async () => {
    // Check if user is logged in
    if (!token || !user) {
      toast.info('Please login to enroll in this course');
      navigate('/login', { state: { from: `/course/${courseId}` } });
      return;
    }

    setEnrolling(true);
    try {
      await axios.post(
        `${API}/courses/${courseId}/enroll`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Successfully enrolled in course!');
      navigate('/courses');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to enroll in course');
    } finally {
      setEnrolling(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 relative">
        {/* Public Navigation Bar */}
        <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm shadow-sm z-50 border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center cursor-pointer" onClick={() => navigate('/')}>
                <Logo className="h-10" />
              </div>
              <div className="hidden md:flex items-center space-x-8">
                <button onClick={() => navigate('/#features')} className="text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors">Features</button>
                <button onClick={() => navigate('/#about')} className="text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors">About</button>
                <button onClick={() => navigate('/#pricing')} className="text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors">Pricing</button>
                <button onClick={() => navigate('/#courses')} className="text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors">Courses</button>
                <button onClick={() => navigate('/login')} className="text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors">Login</button>
                <Button onClick={() => navigate('/register')} size="sm" className="bg-blue-600 hover:bg-blue-700">Get Started</Button>
              </div>
            </div>
          </div>
        </nav>
        <div className="flex items-center justify-center min-h-[60vh] pt-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading course details...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 relative">
        {/* Public Navigation Bar */}
        <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm shadow-sm z-50 border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center cursor-pointer" onClick={() => navigate('/')}>
                <Logo className="h-10" />
              </div>
              <div className="hidden md:flex items-center space-x-8">
                <button onClick={() => navigate('/#features')} className="text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors">Features</button>
                <button onClick={() => navigate('/#about')} className="text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors">About</button>
                <button onClick={() => navigate('/#pricing')} className="text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors">Pricing</button>
                <button onClick={() => navigate('/#courses')} className="text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors">Courses</button>
                <button onClick={() => navigate('/login')} className="text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors">Login</button>
                <Button onClick={() => navigate('/register')} size="sm" className="bg-blue-600 hover:bg-blue-700">Get Started</Button>
              </div>
            </div>
          </div>
        </nav>
        <div className="flex items-center justify-center min-h-[60vh] pt-20">
          <div className="text-center">
            <BookOpen className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Course not found</h2>
            <p className="text-muted-foreground mb-4">The course you're looking for doesn't exist.</p>
            <Button onClick={() => navigate('/')}>Back to Home</Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 relative">
      {/* Public Navigation Bar */}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm shadow-sm z-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center cursor-pointer" onClick={() => navigate('/')}>
              <Logo className="h-10" />
            </div>

            {/* Desktop Navigation Links */}
            <div className="hidden md:flex items-center space-x-8">
              <button onClick={() => navigate('/#features')} className="text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors">
                Features
              </button>
              <button onClick={() => navigate('/#about')} className="text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors">
                About
              </button>
              <button onClick={() => navigate('/#pricing')} className="text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors">
                Pricing
              </button>
              <button onClick={() => navigate('/#courses')} className="text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors">
                Courses
              </button>
              <button onClick={() => navigate('/login')} className="text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors">
                Login
              </button>
              <Button onClick={() => navigate('/register')} size="sm" className="bg-blue-600 hover:bg-blue-700">
                Get Started
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <Button onClick={() => navigate('/login')} size="sm" variant="ghost">
                Login
              </Button>
            </div>
          </div>
        </div>
      </nav>
      
      {/* Hero Section with Course Image */}
      <div className="relative bg-gradient-to-r from-primary/90 to-primary/70 text-white overflow-hidden" style={{ marginTop: '64px' }}>
        {course.image && (
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-20"
            style={{ backgroundImage: `url(${course.image.startsWith('data:') ? course.image : `${course.image}?w=1200&h=400&fit=crop&auto=format&q=50`})` }}
          />
        )}
        <div className="relative max-w-7xl mx-auto px-4 py-12 md:py-16">
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-4">
              <Badge className="bg-white/20 border-white/30 text-white hover:bg-white/30">
                {course.category}
              </Badge>
              {course.is_popular && (
                <Badge className="bg-yellow-400/20 border-yellow-400/30 text-yellow-100 hover:bg-yellow-400/30">
                  <Star className="w-3 h-3 mr-1 fill-current" />
                  Popular
                </Badge>
              )}
            </div>
            <h1 className="text-3xl md:text-5xl font-bold mb-4">{course.title}</h1>
            <p className="text-lg md:text-xl text-white/90 mb-6 leading-relaxed">
              {course.description}
            </p>
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                <Clock className="w-5 h-5" />
                <span className="font-medium">{course.duration}</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                <Target className="w-5 h-5" />
                <span className="font-medium">{course.level}</span>
              </div>
              {course.average_salary_india && (
                <div className="flex items-center gap-2 bg-green-500/20 backdrop-blur-sm px-4 py-2 rounded-lg">
                  <DollarSign className="w-5 h-5" />
                  <span className="font-medium">{course.average_salary_india}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Course Details with Tabs */}
          <div className="lg:col-span-2">
            <Tabs defaultValue="overview" className="w-full">
              <TabsList className="grid w-full grid-cols-4 mb-6">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="syllabus">Syllabus</TabsTrigger>
                <TabsTrigger value="career">Career</TabsTrigger>
                <TabsTrigger value="details">Details</TabsTrigger>
              </TabsList>

              {/* Overview Tab */}
              <TabsContent value="overview" className="space-y-6">
                {/* Course Image */}
                {course.image && (
                  <div className="rounded-xl overflow-hidden shadow-lg bg-gray-100">
                    <img 
                      src={
                        course.image.startsWith('data:')
                          ? course.image
                          : `${course.image}?w=800&h=450&fit=crop&auto=format&q=60`
                      }
                      alt={course.title}
                      loading="lazy"
                      onError={(e) => e.target.style.display = 'none'}
                      className="w-full h-64 object-cover"
                    />
                  </div>
                )}

                <Card className="border-none shadow-md">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                      <BookOpen className="w-5 h-5 text-primary" />
                      Course Overview
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {course.description}
                    </p>
                  </CardContent>
                </Card>

                {/* Prerequisites */}
                {course.prerequisites && course.prerequisites.length > 0 && (
                  <Card className="border-none shadow-md">
                    <CardContent className="p-6">
                      <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                        <Award className="w-5 h-5 text-primary" />
                        Prerequisites
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {course.prerequisites.map((prereq, idx) => (
                          <Badge key={idx} variant="outline" className="px-3 py-1">
                            {prereq}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Skills You'll Gain */}
                {course.skills_gained && course.skills_gained.length > 0 && (
                  <Card className="border-none shadow-md">
                    <CardContent className="p-6">
                      <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                        <Award className="w-5 h-5 text-primary" />
                        Skills You'll Gain
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {course.skills_gained.map((skill, idx) => (
                          <Badge key={idx} className="px-3 py-1 bg-primary/10 text-primary border-primary/20 hover:bg-primary/20">
                            {skill}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              {/* Syllabus Tab */}
              <TabsContent value="syllabus" className="space-y-6">
                <Card className="border-none shadow-md">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold mb-4">Course Syllabus</h3>
                    {course.syllabus_overview && course.syllabus_overview.length > 0 ? (
                      <div className="space-y-3">
                        {course.syllabus_overview.map((topic, idx) => (
                          <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm">
                              {idx + 1}
                            </div>
                            <p className="text-muted-foreground flex-1 pt-1">{topic}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground">Syllabus details will be provided upon enrollment.</p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Career Tab */}
              <TabsContent value="career" className="space-y-6">
                {/* Career Paths */}
                {course.career_paths && course.career_paths.length > 0 && (
                  <Card className="border-none shadow-md">
                    <CardContent className="p-6">
                      <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                        <Briefcase className="w-5 h-5 text-primary" />
                        Career Paths
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {course.career_paths.map((path, idx) => (
                          <div key={idx} className="flex items-center gap-2 p-3 bg-primary/5 rounded-lg">
                            <Check className="w-4 h-4 text-primary flex-shrink-0" />
                            <span className="text-sm font-medium">{path}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Salary Information */}
                <Card className="border-none shadow-md">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-primary" />
                      Salary Expectations
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {course.average_salary_india && (
                        <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                          <p className="text-sm text-muted-foreground mb-1">India Average</p>
                          <p className="text-2xl font-bold text-green-600">{course.average_salary_india}</p>
                        </div>
                      )}
                      {course.average_salary_global && (
                        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                          <p className="text-sm text-muted-foreground mb-1">Global Average</p>
                          <p className="text-2xl font-bold text-blue-600">{course.average_salary_global}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Top Recruiters */}
                {course.top_recruiters && course.top_recruiters.length > 0 && (
                  <Card className="border-none shadow-md">
                    <CardContent className="p-6">
                      <h3 className="text-xl font-bold mb-4">Top Recruiters</h3>
                      <div className="flex flex-wrap gap-3">
                        {course.top_recruiters.map((company, idx) => (
                          <Badge key={idx} variant="outline" className="px-4 py-2 text-sm">
                            {company}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              {/* Details Tab */}
              <TabsContent value="details" className="space-y-6">
                <Card className="border-none shadow-md">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold mb-6">Course Details</h3>
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-muted-foreground mb-1">Duration</p>
                          <p className="font-semibold">{course.duration}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground mb-1">Level</p>
                          <p className="font-semibold">{course.level}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground mb-1">Category</p>
                          <p className="font-semibold">{course.category}</p>
                        </div>
                        {course.price && (
                          <div>
                            <p className="text-sm text-muted-foreground mb-1">Price</p>
                            <p className="font-semibold text-primary">{course.price}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Right Column - Enroll Card (Sticky) */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <Card className="border-none shadow-xl">
                <CardContent className="p-6">
                  <div className="mb-6">
                    <h3 className="text-2xl font-bold mb-2">Ready to Start?</h3>
                    <p className="text-muted-foreground text-sm">
                      Join thousands of students learning this course
                    </p>
                  </div>

                  {course.image && (
                    <div className="mb-6 rounded-lg overflow-hidden bg-gray-100">
                      <img 
                        src={
                          course.image.startsWith('data:')
                            ? course.image
                            : `${course.image}?w=400&h=250&fit=crop&auto=format&q=60`
                        }
                        alt={course.title}
                        loading="lazy"
                        onError={(e) => e.target.style.display = 'none'}
                        className="w-full h-40 object-cover"
                      />
                    </div>
                  )}

                  {/* Course Fee */}
                  {course.price !== undefined && course.price !== null && (
                    <div className="mb-4 p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
                      <p className="text-sm text-gray-600 mb-1">Course Fee</p>
                      <p className="text-3xl font-bold text-blue-600">
                        {course.price === 0 || course.price === '0' ? 'FREE' : `₹${course.price}`}
                      </p>
                      {course.price > 0 && (
                        <p className="text-xs text-gray-500 mt-1">One-time payment</p>
                      )}
                    </div>
                  )}

                  <div className="space-y-3 mb-6">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm text-muted-foreground">Duration</span>
                      <span className="font-semibold">{course.duration}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm text-muted-foreground">Level</span>
                      <span className="font-semibold">{course.level}</span>
                    </div>
                    {/* Average Salary India */}
                    {course.average_salary_india && (
                      <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                        <span className="text-sm text-muted-foreground">Avg. Salary (India)</span>
                        <span className="font-bold text-green-600">{course.average_salary_india}</span>
                      </div>
                    )}
                    {/* Average Salary Global */}
                    {course.average_salary_global && (
                      <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-200">
                        <span className="text-sm text-muted-foreground">Avg. Salary (Global)</span>
                        <span className="font-bold text-purple-600">{course.average_salary_global}</span>
                      </div>
                    )}
                  </div>

                  <Button 
                    onClick={handleEnroll}
                    disabled={enrolling}
                    className="w-full h-12 text-base font-semibold"
                    size="lg"
                  >
                    {enrolling ? (
                      <div className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Enrolling...
                      </div>
                    ) : (
                      <>Enroll Now</>
                    )}
                  </Button>

                  {!token && (
                    <p className="text-xs text-center text-muted-foreground mt-3">
                      You'll be redirected to login
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CourseDetailPublic;
